// The in-call controls panel: the popup that carries mute and end.
//
// Split out of element.ts because it owns a listener on the host document
// (the outside-click dismiss) and therefore owns a teardown obligation. A
// panel that adds a capture-phase document listener on open and forgets to
// remove it on close leaks one listener per call, on a page that is not ours;
// keeping the pair in one class is what makes that hard to get wrong.

import { MIC_ICON } from "./styles.js";

export interface CallControlsCallbacks {
  onToggleMute(): void;
  onCycleSpeed(): void;
  /** End the live call, or abandon a connect that has not produced one yet. */
  onEnd(): void;
  /** Escape pressed inside the panel. */
  onDismiss(): void;
}

export interface CallControlsElements {
  popup: HTMLDivElement;
  body: HTMLDivElement;
  orbDisplay: HTMLDivElement;
  orb: HTMLSpanElement;
  orbWrap: HTMLSpanElement;
  mute: HTMLButtonElement;
  speed: HTMLButtonElement;
  end: HTMLButtonElement;
}

/**
 * Builds the panel. `transcriptElement` is appended into the body: the
 * transcript and the orb display are alternatives, and the renderer picks
 * which one is visible.
 */
export function buildCallControlsDOM(
  transcriptElement: HTMLElement,
  callbacks: CallControlsCallbacks,
): CallControlsElements {
  const popup = document.createElement("div");
  popup.className = "popup";
  // Without a role and a label a screen-reader user has no way to know a
  // panel opened, and without the keydown handler no way to dismiss it -
  // closing was once wired only to a document-level click.
  popup.setAttribute("role", "dialog");
  popup.setAttribute("aria-label", "Call controls");
  popup.addEventListener("keydown", (ev: KeyboardEvent) => {
    if (ev.key !== "Escape") return;
    ev.stopPropagation();
    callbacks.onDismiss();
  });

  const body = document.createElement("div");
  body.className = "popup-body";
  body.appendChild(transcriptElement);

  const orbDisplay = document.createElement("div");
  orbDisplay.className = "popup-orb-display";
  const orbWrap = document.createElement("span");
  orbWrap.className = "orb-wrap";
  const orb = document.createElement("span");
  orb.className = "orb";
  orb.innerHTML = MIC_ICON;
  orbWrap.appendChild(orb);
  orbDisplay.appendChild(orbWrap);
  body.appendChild(orbDisplay);
  popup.appendChild(body);

  const controls = document.createElement("div");
  controls.className = "popup-controls";

  const mute = document.createElement("button");
  mute.type = "button";
  mute.className = "popup-ctrl mute-btn";
  mute.setAttribute("aria-label", "Toggle microphone");
  mute.innerHTML = MIC_ICON;
  mute.addEventListener("click", (e) => {
    e.stopPropagation();
    callbacks.onToggleMute();
  });

  // Hidden unless the embed asks for it AND the agent publishes a speed: an
  // agent whose voice cannot be stretched would otherwise show a control that
  // refuses every press.
  const speed = document.createElement("button");
  speed.type = "button";
  speed.className = "popup-ctrl speed-btn";
  speed.hidden = true;
  speed.setAttribute("aria-label", "Speaking speed");
  speed.addEventListener("click", (e) => {
    e.stopPropagation();
    callbacks.onCycleSpeed();
  });

  const end = document.createElement("button");
  end.type = "button";
  end.className = "popup-ctrl end-btn";
  end.setAttribute("aria-label", "End call");
  end.textContent = "End call";
  end.addEventListener("click", (e) => {
    e.stopPropagation();
    callbacks.onEnd();
  });

  controls.append(mute, speed, end);
  popup.appendChild(controls);

  return { popup, body, orbDisplay, orb, orbWrap, mute, speed, end };
}

/**
 * Open/closed state plus the outside-click dismiss.
 *
 * `host` is the element the click test is relative to: a click whose composed
 * path includes it is inside the widget and must not dismiss. Composed path
 * rather than `contains` because the panel lives in a shadow root, where
 * event targets are retargeted to the host and `contains` cannot see in.
 */
export class CallControls {
  readonly #host: Element;
  readonly #els: CallControlsElements;
  readonly #onChange: () => void;
  #open = false;

  constructor(host: Element, els: CallControlsElements, onChange: () => void) {
    this.#host = host;
    this.#els = els;
    this.#onChange = onChange;
  }

  get isOpen(): boolean {
    return this.#open;
  }

  open(): void {
    if (this.#open) return;
    this.#open = true;
    document.addEventListener("click", this.#onOutsideClick, true);
    this.#onChange();
    // Focus has to enter the panel for its Escape handler to receive the key,
    // and it is what lets a keyboard user reach mute and end at all - the
    // panel was previously only reachable by blind tabbing.
    queueMicrotask(() => {
      if (this.#open) this.#els.end.focus();
    });
  }

  close(): void {
    if (!this.#open) return;
    this.#open = false;
    document.removeEventListener("click", this.#onOutsideClick, true);
    this.#onChange();
  }

  #onOutsideClick = (event: MouseEvent): void => {
    if (!this.#open) return;
    if (event.composedPath().includes(this.#host)) return;
    this.close();
  };
}
